// @flow
import * as actions from './actions';
import * as constants from './constants';
import * as routes from '~/core/constants/routes';
import { alerts, feedback } from '~/core/constants';
import { devices as api, nodes as nodesApi, products as productsAPi } from '~/core/api';
import { all, call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { deviceTypes, hublessDeviceTypes, hubOperationModes } from '~/core/constants/devices';
import { getDashboardCustomisation, saveDashboardEdit, dashboardAddProduct } from '~/core/state/dashboard/actions';
import { getDeviceItems, getDeviceZoneId, getShouldRequestDevices } from './selectors';
import {
    getProducts,
    removeProduct,
    removeProductZone,
    renameProduct,
    renameProductZone
} from '~/core/state/products/actions';
import { getProductItemById } from '~/core/state/products/selectors';
import type { ReceiveDevicesAction, RemoveDeviceAction, RenameDeviceAction, RenameHubDevicesAction } from './types';
import { find, flowRight, noop, some } from 'lodash/fp';
import handleApi from '~/core/state/handle-api';
import handleSaga from '~/core/state/handle-saga';
import type { Effect } from 'redux-saga';
import messages from './intl';
import { navigate } from '~/core/state/navigate';
import { patchProduct } from '~/core/api/products/products.api';
import { RECEIVE_PRODUCTS } from '~/core/state/products/constants';
import { requestAlert } from '~/core/state/alerts/actions';
import { closeSettingsModal } from '~/core/state/settings-modal/actions';
import { PRIMARY, SECONDARY } from '~/core/state/settings-modal/actions';
import { map, toJS } from '~/core/utils/immutable';
import { getConfig } from '../../state/config/selectors';
import { getAvailableProductsForCustomisation } from '~/core/state/dashboard/selectors';
import { progressInstallStage } from '~/core/state/install-nano1-upgrade/actions';
import { STAGE_STATUS } from '~/core/state/install-nano1-upgrade/constants';

//////// Format Functions
const formatDevices = devices => (Array.isArray(devices) ? devices.map(addSignalToHub) : devices);

function addSignalToHub(device) {
    return device.type === deviceTypes.HUB ? addSignalToProp(device.props.online, device) : device;
}

function addSignalToProp(isOnline, device) {
    device.props.signal = isOnline ? 100 : 0;
    return device;
}

// Sagas
export const requestDevicesSaga = function*(): Generator<Effect, *, *> {
    const putReceiveDevices = flowRight(put, actions.receiveDevices, formatDevices);
    const shouldFetchDevices = yield select(getShouldRequestDevices);

    if (!shouldFetchDevices) {
        return;
    }

    yield put(actions.requestDevices());
    const [devices, error] = yield call(handleApi(api.getDevices));
    yield putReceiveDevices(error ? error : devices);

    if (error) {
        yield put(requestAlert(messages.error, alerts.types.ERROR));
    }
};

const watchRequestDevices = function*(): Generator<Effect, *, *> {
    yield takeEvery(
        [constants.LOAD_INSTALL_OPTIONS, constants.LOAD_MANAGE_DEVICES, constants.LOAD_DEVICES],
        handleSaga(requestDevicesSaga)
    );
};

export function* renameDevice(
    { payload: { name, productType, productId } }: RenameDeviceAction,
    action: noop,
    productAction: noop,
    zoneId?: string
): Generator<Effect, *, *> {
    const [res, error] = yield call(handleApi(patchProduct), { name }, productType, productId);

    if (error) {
        yield put(action(error));
        yield put(requestAlert(messages.nameChangeError, alerts.types.ERROR));
        return;
    }

    const disallowRedirect = yield select(getConfig, 'features:manageDevicesModal');

    yield put(action({ productId, name }));
    yield put(productAction({ productId, name, zoneId }));

    if (process.env.PLATFORM === 'web') {
        yield put(closeSettingsModal({ modal: SECONDARY }));
    }

    if (!disallowRedirect && res) {
        yield put(navigate(`/devices#${productId}`));
    }

    if (!res) {
        yield put(requestAlert(messages.nameChangeError, alerts.types.ERROR));
    }
}

export function* renameDeviceSaga(payload: RenameDeviceAction): Generator<Effect, *, *> {
    yield* renameDevice(payload, actions.renamedDevice, renameProduct);
}

function* watchRenameDeviceSaga(): Generator<Effect, *, *> {
    yield takeLatest(constants.RENAME_DEVICE, handleSaga(renameDeviceSaga));
}

export function* renameHubDevicesSaga({
    payload: devicesWithNames
}: RenameHubDevicesAction): Generator<Array<*> | Effect, *, *> {
    let placementAdviceNeeded = false;

    if (devicesWithNames.length) {
        const responses = yield all(
            devicesWithNames.map(({ device, newName }) =>
                call(handleApi(nodesApi.patchNode), { name: newName }, device.type, device.id)
            )
        );

        const hasErrors = some(([, error]) => error, responses);

        if (hasErrors) {
            yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
            yield put(navigate(routes.install));
        } else {
            const currentDevices = yield select(getDeviceItems);
            const currentDevicesWithNewNames = currentDevices.map(device => {
                const { id } = device;
                const renamed = devicesWithNames.filter(({ device: { id: renamedId } }) => {
                    return renamedId === id;
                });
                if (renamed.length) {
                    const [renamedDevice] = renamed.map(d => {
                        const { device: { state, ...renamedRest }, newName } = d;
                        return {
                            ...renamedRest,
                            state: {
                                ...state,
                                name: newName
                            }
                        };
                    });
                    return renamedDevice;
                }
                return device;
            });
            placementAdviceNeeded = currentDevicesWithNewNames.some(({ type }) => type === deviceTypes.SIGNAL_BOOSTER);
            yield put(actions.receiveDevices(currentDevicesWithNewNames));
        }
    } else {
        const currentDevices = yield select(getDeviceItems);
        placementAdviceNeeded = currentDevices.some(({ type }) => type === deviceTypes.SIGNAL_BOOSTER);
    }
    yield put(getProducts());
    yield put(getDashboardCustomisation());

    const { error } = yield take(RECEIVE_PRODUCTS); // Don't proceed to dashboard until we receive products.

    if (error) {
        return;
    }

    if (placementAdviceNeeded) {
        yield put(navigate(routes.installDevicePlacement));
    } else {
        yield put(navigate(routes.dashboard));
    }
}

function* watchRenameHubDeviceaSaga(): Generator<Effect, *, *> {
    yield takeLatest(constants.RENAME_HUB_DEVICES, handleSaga(renameHubDevicesSaga));
}

export function* renameZoneSaga(payload: RenameDeviceAction): Generator<Effect, *, *> {
    const zoneId = yield select(getDeviceZoneId, {
        id: payload.payload.productId
    });
    yield* renameDevice(payload, actions.renamedZone, renameProductZone, zoneId);
}

function* watchRenameZoneSaga(): Generator<Effect, *, *> {
    yield takeLatest(constants.RENAME_ZONE, handleSaga(renameZoneSaga));
}

export function* removeDevice(
    { payload: { id, allowRedirect = true } }: RemoveDeviceAction,
    deleteApi: *,
    action: noop,
    productAction: noop
): Generator<Effect, *, *> {
    const [, error] = yield call(handleApi(deleteApi), id);
    const device = yield select(getProductItemById, { id });
    const isGroup = device && device.get('isGroup');

    if (error) {
        yield put(requestAlert(isGroup ? messages.groupRemoveError : messages.deviceRemoveError, alerts.types.ERROR));
        yield put(actions.removeDeviceError());
        return;
    }

    if (isGroup) {
        const availableDashboardProducts = yield select(getAvailableProductsForCustomisation);
        // Get the list of products that are included on the group and are not already on the dashboard
        const includedProducts = device
            .getIn(['props', 'consumers'])
            .toSet()
            .intersect(availableDashboardProducts);

        const addProduct = flowRight(put, dashboardAddProduct);
        const addAllDevicesToDashboard = flowRight(all, toJS, map(addProduct));

        yield addAllDevicesToDashboard(includedProducts);
        yield put(saveDashboardEdit());
    }

    yield put(closeSettingsModal({ modal: PRIMARY }));

    if (allowRedirect) {
        yield put(navigate(routes.devices));
    }

    yield put(requestAlert(isGroup ? messages.groupRemoved : messages.deviceRemoved, alerts.types.SUCCESS));
    yield put(action({ id }));
    yield put(productAction({ id, zoneId: id }));
    yield put(saveDashboardEdit());
    yield put(getProducts());
}

export function* removeDeviceSaga(payload: RemoveDeviceAction): Generator<Effect, *, *> {
    yield* removeDevice(payload, api.deleteDevice, actions.removedDevice, removeProduct);
}

function* watchRemoveDeviceSaga(): Generator<Effect, *, *> {
    yield takeLatest(constants.REMOVE_DEVICE, handleSaga(removeDeviceSaga));
}

export function* removeZoneSaga(payload: RemoveDeviceAction): Generator<Effect, *, *> {
    yield* removeDevice(payload, api.deleteZone, actions.removedZone, removeProductZone);
}

function* watchRemoveZoneSaga(): Generator<Effect, *, *> {
    yield takeLatest(constants.REMOVE_ZONE, handleSaga(removeZoneSaga));
}

export function* receiveDevicesSaga({ payload }: ReceiveDevicesAction): Generator<Effect, *, *> {
    const hub = find(x => x.type === deviceTypes.HUB, payload);

    if (!hub) {
        return;
    }

    const isOnline = hub.props.online;
    const isMigrating = [hubOperationModes.FAILURE, hubOperationModes.MIGRATING].includes(hub.props.operation);

    if (!isOnline) {
        yield put(requestAlert(messages.hubOfflineError, alerts.types.ERROR, true));
        const hasHublessDevice = payload.filter(d => hublessDeviceTypes.includes(d.type));

        if (hasHublessDevice.length > 0) {
            return;
        }

        yield put(navigate(routes.devices));
    } else if (isMigrating) {
        yield put(navigate(routes.upgradeNano1Hub));
        yield put(progressInstallStage(STAGE_STATUS));
    }
}

function* watchReceiveDevicesSaga(): Generator<Effect, *, *> {
    yield takeLatest(constants.RECEIVE_DEVICES, handleSaga(receiveDevicesSaga));
}

export function* restartHubSaga({ payload: { id } }: { payload: { id: string } }): Generator<Effect, *, *> {
    const [, error] = yield call(handleApi(productsAPi.restartHub), id);

    if (error) {
        yield put(requestAlert(messages.error, alerts.types.ERROR));
    }

    yield put(actions.restartedHub(error || ''));
}

function* watchRestartHubSaga(): Generator<Effect, *, *> {
    yield takeLatest(constants.RESTART_HUB, handleSaga(restartHubSaga));
}

export default [
    watchReceiveDevicesSaga,
    watchRemoveDeviceSaga,
    watchRemoveZoneSaga,
    watchRenameDeviceSaga,
    watchRenameHubDeviceaSaga,
    watchRenameZoneSaga,
    watchRequestDevices,
    watchRestartHubSaga
];



// WEBPACK FOOTER //
// ./src/core/state/devices/sagas.js